import React, { useState } from "react";
import toast from "react-hot-toast";
import validator from "validator";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiMail, FiEye, FiEyeOff, FiLock, FiUser } from "react-icons/fi";

import { setUser, setApp } from "../../redux/auth/actions";

import IconSpace1 from "../../assets/spaces-icons/1.png";
import IconSpace2 from "../../assets/spaces-icons/2.png";
import IconSpace4 from "../../assets/spaces-icons/4.png";
import IconSpace5 from "../../assets/spaces-icons/5.png";
import IconSpace6 from "../../assets/spaces-icons/6.png";
import selegoLogo from "../../assets/selego.png";

import api from "../../services/api";
import Loader from "../../components/loader";

export default function Signin() {
  const [email, setEmail] = useState("");
  const [userExists, setUserExists] = useState(null);
  const [forgotPassword, setForgotPassword] = useState(false);

  const [searchParams] = useSearchParams();

  const user = useSelector((state) => state.Auth.user);
  const redirect = searchParams.get("redirect");
  const appParam = searchParams.get("application");
  const appInfo = appParam ? applications[appParam] : applications.accounting;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 flex items-center justify-center">
      <div className="relative max-w-xl w-full">
        <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-xl border border-blue-500/20 shadow-2xl p-10">
          {appInfo ? (
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center">
                <img src={appInfo.icon} alt={appInfo.name} className="w-16 h-16" />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center">
                <img src={selegoLogo} alt="Selego" className="w-16 h-16" />
              </div>
            </div>
          )}

          {!email && !forgotPassword && <EmailCheck setEmail={setEmail} setUserExists={setUserExists} appInfo={appInfo} />}
          {userExists && email && !forgotPassword && <SignIn email={email} setForgotPassword={setForgotPassword} appInfo={appInfo} />}
          {!userExists && email && !forgotPassword && <SignUp email={email} appInfo={appInfo} />}
          {forgotPassword && <ForgotPassword setForgotPassword={setForgotPassword} email={email} />}
        </div>
      </div>
    </div>
  );
}

// Email Check Component
const EmailCheck = ({ setEmail, setUserExists, appInfo }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmailValue] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!email) return setError("Email is required");
      if (!validator.isEmail(email)) return setError("Invalid email address");
      setLoading(true);

      const { exists } = await api.post("/user/check-email", { email });
      setEmail(email);
      setUserExists(exists);
    } catch (error) {
      if (error.code === "INVITATION_NOT_ACCEPTED") return toast.error("You have been invited to join an organisation. Please check your email for the invitation.");
      console.error(error);
      toast.error("Error checking email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-white">{`Welcome to ${appInfo.name}`}</h1>
        <p className="text-gray-400 text-base mt-2">Enter your email to get started</p>
      </div>
      <div className="space-y-8">
        <div>
          <label className="text-base text-gray-300 flex items-center mb-2">
            <FiMail className="mr-2 text-blue-400" size={20} />
            Work email <span className="text-blue-400 ml-1">*</span>
          </label>
          <input
            className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base"
            name="email"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="example@mail.com"
          />
          {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
        </div>
        <button className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105" type="submit">
          {loading ? <Loader size="small" color="white" /> : "Continue"}
        </button>
      </div>
    </form>
  );
};

// Sign In Component
const SignIn = ({ email, setForgotPassword, appInfo }) => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [seePassword, setSeePassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!password) return setError("Password is required");
      setLoading(true);

      const { user, token } = await api.post(`/user/signin`, { email, password });
      if (token) api.setToken(token);
      if (user) dispatch(setUser(user));

      const application = appInfo.id !== "accounting" ? appInfo.id : user.application || "accounting";
      dispatch(setApp(application));

      if (redirect && redirect.startsWith("http")) window.location.href = redirect;
      else if (redirect) navigate(redirect);
      else navigate(applications[application].url);
    } catch (error) {
      console.error(error);
      toast.error("Error while signing in");
    }
    setLoading(false);
  };

  return (
    <form className="py-2 px-8 w-full" onSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-white">{`Welcome to ${appInfo.name}`}</h1>
        <p className="text-gray-400 text-base mt-2">Welcome back! Enter your password to continue</p>
      </div>
      <div className="mt-10 space-y-8">
        <div>
          <label className="text-base text-gray-300 flex items-center mb-2">
            <FiMail className="mr-2 text-blue-400" size={20} />
            Email
          </label>
          <input className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 text-base" name="email" type="email" id="email" value={email} disabled />
        </div>
        <div>
          <label className="text-base text-gray-300 flex items-center mb-2">
            <FiLock className="mr-2 text-blue-400" size={20} />
            Password <span className="text-blue-400 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base"
              name="password"
              type={seePassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            <button type="button" onClick={() => setSeePassword(!seePassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400">
              {seePassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
          <div className="text-right mt-2">
            <button type="button" onClick={() => setForgotPassword(true)} className="text-sm text-blue-400 hover:text-blue-300">
              Forgot password?
            </button>
          </div>
        </div>
        <button className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105" type="submit">
          {loading ? <Loader size="small" color="white" /> : "Sign in"}
        </button>
      </div>
    </form>
  );
};

// Forgot Password Component
const ForgotPassword = ({ setForgotPassword, email }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailValue, setEmailValue] = useState(email || "");
  const [error, setError] = useState("");

  const validateEmail = (value) => {
    if (!validator.isEmail(value)) {
      return "Invalid email address";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const validationError = validateEmail(emailValue);
      if (validationError) {
        setError(validationError);
        return;
      }

      setLoading(true);
      await api.post("/user/forgot_password", { email: emailValue });
      toast.success("Password recovery link has been sent to your email");
      setDone(true);
    } catch (error) {
      console.error(error);
      toast.error("Error sending recovery link");
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="py-8 px-8 w-full">
        <div className="text-center space-y-6">
          <h1 className="text-2xl font-semibold text-white">Recovery Link Sent</h1>
          <p className="text-gray-400 text-base">Password recovery link has been sent to your email. Please check your inbox and follow the link to reset your password.</p>
        </div>
        <div className="mt-8">
          <button
            onClick={() => setForgotPassword(false)}
            className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105">
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="py-8 px-8 w-full" onSubmit={handleSubmit}>
      <div className="text-center space-y-6">
        <h1 className="text-2xl font-semibold text-white">Forgot Password</h1>
        <p className="text-gray-400 text-base">Enter your email address below to receive the password reset link.</p>
      </div>
      <div className="mt-8 space-y-8">
        <div>
          <label className="text-base text-gray-300 flex items-center mb-2">
            <FiMail className="mr-2 text-blue-400" size={20} />
            Email <span className="text-blue-400 ml-1">*</span>
          </label>
          <input
            className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base"
            name="email"
            type="email"
            id="email"
            value={emailValue}
            onChange={(e) => {
              setEmailValue(e.target.value);
              setError("");
            }}
            placeholder="example@mail.com"
          />
          {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
        </div>
        <button className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105" type="submit">
          {loading ? <Loader size="small" color="white" /> : "Send Recovery Link"}
        </button>
        <div className="w-full flex items-center my-2">
          <div className="border-b border-gray-700 w-full" />
          <span className="px-2 text-gray-500 text-xs">or</span>
          <div className="border-b border-gray-700 w-full" />
        </div>
        <button
          type="button"
          onClick={() => setForgotPassword(false)}
          className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-gray-800 hover:bg-gray-700">
          Back to Sign In
        </button>
      </div>
    </form>
  );
};

// Sign Up Component
const SignUp = ({ email, appInfo }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ email, password: "", first_name: "", last_name: "" });
  const [errors, setErrors] = useState({ password: "", first_name: "", last_name: "" });
  const [seePassword, setSeePassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const waalego_referred_by = searchParams.get("ref");
  const utm_source = searchParams.get("utm_source");
  const utm_from = searchParams.get("utm_from");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!values.first_name) return setErrors({ ...errors, first_name: "First name is required" });
      if (!values.last_name) return setErrors({ ...errors, last_name: "Last name is required" });
      if (!values.password) return setErrors({ ...errors, password: "Password is required" });
      setLoading(true);

      const application = appInfo.id !== "accounting" ? appInfo.id : "accounting";
      const data = { ...values, application, name: `${values.first_name} ${values.last_name}` };
      delete data.first_name;
      delete data.last_name;

      if (application === "waalego") {
        data.waalego_referred_by = waalego_referred_by;
        data.waalego_utm_source = utm_source;
        data.waalego_utm_from = utm_from;
        data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      if (application !== "accounting") data[application] = true;

      const { user, token } = await api.post("/user/signup", data);

      if (token) api.setToken(token);
      if (user) dispatch(setUser(user));

      dispatch(setApp(application));
      
      navigate(applications[application].url);

      toast.success("Account created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error while signing up");
    }
    setLoading(false);
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-white">{`Welcome to ${appInfo.name}`}</h1>
        <p className="text-gray-400 text-sm mt-4">Complete your registration to get started</p>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-gray-300 flex items-center mb-1">
            <FiMail className="mr-2 text-blue-400" size={18} />
            Work email
          </label>
          <input className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 text-sm" name="email" type="email" value={email} disabled />
        </div>
        <div>
          <label className="text-sm text-gray-300 flex items-center mb-1">
            <FiUser className="mr-2 text-blue-400" size={18} />
            Full Name <span className="text-blue-400 ml-1">*</span>
          </label>
          <div className="flex items-center gap-2 w-full">
            <input
              className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-white text-sm"
              name="first_name"
              type="text"
              value={values.first_name}
              onChange={handleChange}
              placeholder="Enter your first name"
            />
            <input
              className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-white text-sm"
              name="last_name"
              type="text"
              value={values.last_name}
              onChange={handleChange}
              placeholder="Enter your surname"
            />
          </div>
          <div className="flex flex-col">
            {errors.first_name && <p className="text-xs text-red-400 mt-1">{errors.first_name}</p>}
            {errors.last_name && <p className="text-xs text-red-400 mt-1">{errors.last_name}</p>}
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-300 flex items-center mb-1">
            <FiLock className="mr-2 text-blue-400" size={18} />
            Password <span className="text-blue-400 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-white text-sm"
              name="password"
              type={seePassword ? "text" : "password"}
              value={values.password}
              onChange={handleChange}
              placeholder="Choose a secure password"
            />
            <button type="button" onClick={() => setSeePassword(!seePassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400">
              {seePassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
        </div>
        <div className="bg-blue-500/10 p-2 rounded-lg border border-blue-500/20 text-xs text-gray-300">
          By signing up, you agree to our{" "}
          <a href={appInfo.terms} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
            Terms and Privacy Policy
          </a>
        </div>
        <button className="w-full py-2 rounded-lg text-sm font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700" type="submit">
          {loading ? <Loader size="small" color="white" /> : "Create Account"}
        </button>
      </div>
    </form>
  );
};

const applications = {
  accounting: {
    id: "accounting",
    name: "Selego",
    icon: IconSpace1,
    url: "/",
    subtitle: "Financial management",
    terms: "https://app.selego.co/terms-privacy",
  },
  kotogo: {
    id: "kotogo",
    name: "Kotogo",
    icon: IconSpace1,
    url: "/kotogo",
    subtitle: "Slide generator",
    terms: "https://app.selego.co/terms-privacy",
  },
  
  selecole: {
    id: "selecole",
    name: "Selecole",
    icon: IconSpace4,
    url: "/selecole",
    subtitle: "Education platform",
    terms: "https://app.selego.co/terms-privacy",
  },
  rfp: {
    id: "rfp",
    name: "RFP",
    icon: IconSpace6,
    url: "/missions_ao/search",
    subtitle: "Request for proposals",
    terms: "https://app.selego.co/terms-privacy",
  },
};
